import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getFormValues } from 'redux-form';
import { cloneDeep } from 'lodash';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import { createOperator, deleteOperator, getAllOperators, getOperatorsList, updateOperator } from './operators.actions';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { Forms } from '../../form/form.selector';
import { getFarmsListRequest } from '../farms/farms.actions';

export function* getOperatorsListSaga() {
  yield put(showLoader(LoadersNames.fetchOperators));
  try {
    const result = yield call(api.operator.getOperatorsList);
    yield put(getOperatorsList.done({ params: {}, result }));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchOperators));
}

export function* getAllOperatorsSaga() {
  yield put(showLoader(LoadersNames.fetchOperators));
  try {
    const result = yield call(api.operator.getOperatorsList, true);
    yield put(getAllOperators.done({ params: {}, result }));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchOperators));
}

export function* createOperatorSaga({ meta, payload: formName }: ReturnType<typeof createOperator.started>) {
  yield put(showLoader(LoadersNames.updateItem));
  const operator = yield select(getFormValues(Forms.operatorsForm));
  const payload = {
    ...operator,
    tapUserId: !operator.bindToUser || operator.tapUserId === '-1' ? '' : operator.tapUserId,
  };
  try {
    const result = yield call(api.operator.createOperator, payload, formName);
    yield put(createOperator.done({ params: formName, result }));
    successHandler(meta);
    yield put(notificationSuccess('operators:create_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }

  yield put(hideLoader(LoadersNames.updateItem));
}

export function* deleteOperatorSaga({ payload, meta }: ReturnType<typeof deleteOperator.started>) {
  try {
    const { id, formName } = payload;
    const result = yield call(api.operator.deleteOperator, id, formName);
    yield put(deleteOperator.done({ params: payload, result }));
    successHandler(meta);
    yield put(notificationSuccess('operators:delete_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* updateOperatorSaga({ payload, meta }: ReturnType<typeof updateOperator.started>) {
  yield put(showLoader(LoadersNames.updateItem));
  const data = cloneDeep(payload.data);
  try {
    if (!data.bindToUser || data.tapUserId === '-1') {
      data.tapUserId = '';
    }
    const result = yield call(api.operator.updateOperator, data.id, data, payload.formName);
    yield put(updateOperator.done({ params: payload, result }));
    successHandler(meta);
    yield put(notificationSuccess('operators:update_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }

  yield put(hideLoader(LoadersNames.updateItem));
}

export function* watchOperatorsSaga() {
  yield all([
    yield takeLatest(getFarmsListRequest.done, getAllOperatorsSaga),
    yield takeLatest(getAllOperators.started, getAllOperatorsSaga),
    yield takeLatest(getOperatorsList.started, getOperatorsListSaga),
    yield takeLatest(createOperator.started, createOperatorSaga),
    yield takeLatest(deleteOperator.started, deleteOperatorSaga),
    yield takeLatest(updateOperator.started, updateOperatorSaga),
  ]);
}
